import axios from 'axios';
import { apiClient } from './client';
import { effectiveMimeType, type UploadedAttachment } from './attachments';
import type {
  Ticket, TicketStatus, TicketPriority, PaginatedResponse,
  TicketCollaborator, TicketCollaboratorInvite,
} from '../types';

/** A ticket's collaborators, plus invites still waiting to be claimed. */
export interface CollaboratorList {
  collaborators: TicketCollaborator[];
  invites: TicketCollaboratorInvite[];
}

/** Someone at the ticket's organization who could be added to it. */
export interface CollaboratorCandidate {
  contactId: string;
  name: string;
  email: string | null;
  /** Set once they have signed in here; null means they get invited. */
  userId: string | null;
  alreadyOn: boolean;
  invited: boolean;
}

export interface TicketFilters {
  status?: string;
  /** CSV of semantic categories (new,open,waiting_customer,waiting_internal,resolved,closed). */
  statusCategory?: string;
  priority?: string;
  type?: string;
  impact?: string;
  assignedTo?: string;
  categoryId?: string;
  /** Comma-separated ProductLine ids; "none" selects tickets without one. */
  productLineId?: string;
  search?: string;
  /** ISO 8601 creation window. */
  createdAfter?: string;
  createdBefore?: string;
  /** Tickets the caller has an unsent compose-box draft on. */
  draft?: 'mine';
  /** Tickets in a waiting-on-customer status that are waiting on the caller. */
  waitingOnMe?: '1';
  /** Tickets with an open sub-ticket task assigned to the caller. */
  taskForMe?: '1';
  page?: number;
  limit?: number;
  sort?: string;
}

/** The caller's unsent compose-box draft on a ticket (server-side). */
export interface TicketDraft {
  id: string;
  ticketId: string;
  type: 'reply' | 'internal_note';
  content: string;
  attachments: UploadedAttachment[];
  status: string | null;
  updatedAt: string;
}

export interface TicketDraftInput {
  type: 'reply' | 'internal_note';
  content: string;
  attachments: UploadedAttachment[];
  status: string | null;
}

/** Part picker for Send to Claude: what goes into the dispatched prompt.
 *  `null` id-lists mean "all"; an empty array means "none". */
export interface AiDispatchSelection {
  facts: boolean;               // environment facts (version/env/OS/browser/device/reproducibility/impact)
  stepsToReproduce: boolean;
  resolution: boolean;
  messageIds: string[] | null;
  attachmentIds: string[] | null;
}

export const ticketsApi = {
  list: async (filters: TicketFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v !== undefined && params.set(k, String(v)));
    const res = await apiClient.get(`/tickets?${params}`);
    return res.data.data as PaginatedResponse<Ticket> & { tickets: Ticket[] };
  },

  get: async (id: string) => {
    const res = await apiClient.get(`/tickets/${id}`);
    return res.data.data.ticket as Ticket;
  },

  /** `taskTemplateId`: omit = the auto-apply checklist for the type / product
   *  line (if any), null = no checklist, id = that template. */
  create: async (data: Partial<Ticket> & { title: string; description: string; coAssigneeIds?: string[]; attachments?: { filename: string; url: string; size: number; mimeType: string }[]; taskTemplateId?: string | null }) => {
    const res = await apiClient.post('/tickets', data);
    return res.data.data.ticket as Ticket;
  },

  update: async (id: string, data: Partial<Ticket> & { assignedToId?: string | null; assignedTeamId?: string | null; contactId?: string | null; coAssigneeIds?: string[] }) => {
    const res = await apiClient.put(`/tickets/${id}`, data);
    return res.data.data.ticket as Ticket;
  },

  updateStatus: async (
    id: string,
    status: TicketStatus,
    extra?: { resolution?: string; closedReason?: string },
  ) => {
    const res = await apiClient.patch(`/tickets/${id}/status`, { status, ...extra });
    return res.data.data.ticket as Ticket;
  },

  delete: async (id: string) => {
    await apiClient.delete(`/tickets/${id}`);
  },

  getStats: async () => {
    const res = await apiClient.get('/tickets/stats/summary');
    return res.data.data;
  },

  getMessages: async (ticketId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/messages`);
    return res.data.data.messages;
  },

  /** `status` moves the ticket in the same request; the message body doubles
   *  as the resolution / closing reason the target status may require.
   *  `linkedTicketIds` cross-posts the same message onto those linked tickets. */
  createMessage: async (
    ticketId: string,
    data: { content: string; type?: string; attachments?: any[]; status?: TicketStatus; includeContent?: boolean; linkedTicketIds?: string[]; assignedToId?: string | null },
  ) => {
    const res = await apiClient.post(`/tickets/${ticketId}/messages`, data);
    return res.data.data as {
      message: any;
      status?: { key: string; label: string };
      /** Present only when this update reassigned the ticket; null = unassigned. */
      assignedTo?: { id: string; name: string } | null;
      crossPosted?: Array<{ ticketId: string; ticketNumber: string }>;
    };
  },

  // Compose-box draft: read on open, PUT (upsert) as the user types, DELETE
  // to discard. Sending a message removes it server-side.
  getDraft: async (ticketId: string): Promise<TicketDraft | null> => {
    const res = await apiClient.get(`/tickets/${ticketId}/draft`);
    return res.data.data.draft ?? null;
  },

  saveDraft: async (ticketId: string, data: TicketDraftInput): Promise<TicketDraft | null> => {
    const res = await apiClient.put(`/tickets/${ticketId}/draft`, data);
    return res.data.data.draft ?? null;
  },

  deleteDraft: async (ticketId: string): Promise<void> => {
    await apiClient.delete(`/tickets/${ticketId}/draft`);
  },

  updateMessage: async (ticketId: string, messageId: string, content: string) => {
    const res = await apiClient.put(`/tickets/${ticketId}/messages/${messageId}`, { content });
    return res.data.data.message;
  },

  deleteMessage: async (ticketId: string, messageId: string) => {
    await apiClient.delete(`/tickets/${ticketId}/messages/${messageId}`);
  },

  addRelated: async (ticketId: string, relatedTicketId: string, relationType = 'related') => {
    const res = await apiClient.post(`/tickets/${ticketId}/related`, { relatedTicketId, relationType });
    return res.data.data.ticket as Ticket;
  },

  /** Link several tickets at once; returns the refreshed ticket. */
  linkTickets: async (ticketId: string, relatedTicketIds: string[]) => {
    const res = await apiClient.post(`/tickets/${ticketId}/related`, { relatedTicketIds });
    return res.data.data as { ticket: Ticket; linked: Array<{ id: string; ticketNumber: string }> };
  },

  removeRelated: async (ticketId: string, relatedId: string) => {
    await apiClient.delete(`/tickets/${ticketId}/related/${relatedId}`);
  },

  /** Keyword-ranked similar tickets (staff only). */
  getSimilar: async (ticketId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/similar`);
    return res.data.data.tickets as Array<{
      id: string; ticketNumber: string; title: string; status: string; statusCategory: string; createdAt: string; score: number;
    }>;
  },

  /** Resolve typed ticket numbers ("12", "cTKT-00012", comma/space mixed). */
  lookupByNumbers: async (numbers: string) => {
    const res = await apiClient.get('/tickets/lookup', { params: { numbers } });
    return res.data.data.tickets as Array<{
      id: string; ticketNumber: string; title: string; status: string; statusCategory: string; createdAt: string;
    }>;
  },

  getActivity: async (ticketId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/activity`);
    return res.data.data.activities;
  },

  addCc: async (ticketId: string, data: { email: string; name?: string; userId?: string; crmContactId?: string }) => {
    const res = await apiClient.post(`/tickets/${ticketId}/cc`, data);
    return res.data.data.cc;
  },

  removeCc: async (ticketId: string, ccId: string) => {
    await apiClient.delete(`/tickets/${ticketId}/cc/${ccId}`);
  },

  aiPrompt: async (ticketId: string): Promise<string> => {
    const res = await apiClient.get(`/tickets/${ticketId}/ai-prompt`);
    return res.data.data.prompt;
  },

  // Exact prompt a dispatch with these options would produce — nothing is
  // queued. Backs the Send to Claude dialog's prompt preview. productLineId
  // is the destination override: omit = the ticket's own line, null = none.
  aiPromptPreview: async (
    ticketId: string,
    opts: { selection?: AiDispatchSelection; planFirst?: boolean; productLineId?: string | null } = {},
  ): Promise<string> => {
    const res = await apiClient.post(`/tickets/${ticketId}/ai-prompt`, {
      ...(opts.selection ? { selection: opts.selection } : {}),
      ...(opts.planFirst === false ? { planFirst: false } : {}),
      ...(opts.productLineId !== undefined ? { productLineId: opts.productLineId } : {}),
    });
    return res.data.data.prompt;
  },

  aiDispatch: async (
    ticketId: string,
    opts: {
      agent?: string;
      selection?: AiDispatchSelection;
      planFirst?: boolean;
      productLineId?: string | null;  // destination override; omit = ticket's own line
      repoPath?: string | null;       // exact folder on the agent's machine
    } = {},
  ): Promise<{ queued: boolean; alreadyQueued: boolean; position: number; agentOnline: boolean; agent: string | null }> => {
    const res = await apiClient.post(`/tickets/${ticketId}/ai-dispatch`, {
      ...(opts.agent ? { agent: opts.agent } : {}),
      ...(opts.selection ? { selection: opts.selection } : {}),
      ...(opts.planFirst === false ? { planFirst: false } : {}),
      ...(opts.productLineId !== undefined ? { productLineId: opts.productLineId } : {}),
      ...(opts.repoPath ? { repoPath: opts.repoPath } : {}),
    });
    return res.data.data;
  },

  // Named dispatch agents currently online (for the Send-to-Claude target
  // picker). anyOnline also covers old unnamed agents.
  aiDispatchAgents: async (): Promise<{ agents: string[]; anyOnline: boolean }> => {
    const res = await apiClient.get('/tickets/ai-dispatch/agents');
    return res.data.data;
  },

  exportPdf: async (ticketId: string): Promise<Blob> => {
    const res = await apiClient.get(`/tickets/${ticketId}/export/pdf`, { responseType: 'blob' });
    return res.data as Blob;
  },

  share: async (ticketId: string, recipients: { email: string; name?: string }[]) => {
    await apiClient.post(`/tickets/${ticketId}/share`, { recipients });
  },

  // Collaborators: people attached to this ticket so they can see it, its
  // tasks and task notes, and work the tasks. They are NOT made members of
  // the company. Adding/removing is staff-only.
  getCollaborators: async (ticketId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/collaborators`);
    return res.data.data as CollaboratorList;
  },

  /** Who may collaborate on this ticket: the contacts of its organization. */
  getCollaboratorCandidates: async (ticketId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/collaborators/candidates`);
    return res.data.data as {
      organization: { id: string; name: string } | null;
      contacts: CollaboratorCandidate[];
    };
  },

  /** Add by CRM contact id and/or email. Both must belong to the ticket's
   *  organization; someone without an account yet is invited and attaches
   *  itself when they first sign in. */
  addCollaborators: async (ticketId: string, payload: { contactIds?: string[]; emails?: string[] }) => {
    const res = await apiClient.post(`/tickets/${ticketId}/collaborators`, payload);
    return res.data.data as CollaboratorList & {
      organization: { id: string; name: string };
      added: number; invited: number; skipped: string[];
    };
  },

  removeCollaborator: async (ticketId: string, userId: string) => {
    const res = await apiClient.delete(`/tickets/${ticketId}/collaborators/${userId}`);
    return res.data.data as CollaboratorList;
  },

  /** Withdraw an invite that has not been claimed yet. */
  removeCollaboratorInvite: async (ticketId: string, inviteId: string) => {
    const res = await apiClient.delete(`/tickets/${ticketId}/collaborators/invites/${inviteId}`);
    return res.data.data as CollaboratorList;
  },
};

// Plain axios — no session cookie, no X-Company-Id, no auto-redirect on 401.
// The token *is* the auth here; an expired token should show an inline
// "link expired" message, not bounce the user to /login.
const publicHttp = axios.create({ baseURL: '/api', timeout: 30_000 });

export interface PublicReplyAttachment {
  filename: string;
  url: string;
  size: number;
  mimeType: string;
  path: string;
}

export const publicTicketApi = {
  view: async (token: string) => {
    const res = await publicHttp.get(`/portal/tickets/view/${token}`);
    return res.data.data as {
      viewerEmail: string;
      ticket: any;
    };
  },

  reply: async (token: string, payload: { content: string; attachments?: PublicReplyAttachment[] }) => {
    const res = await publicHttp.post(`/portal/tickets/view/${token}/reply`, payload);
    return res.data.data.message;
  },

  // Upload a file via the signed ticket token (no session). Mirrors the
  // authenticated two-step presign+PUT flow.
  uploadAttachment: async (token: string, file: File): Promise<PublicReplyAttachment> => {
    const contentType = effectiveMimeType(file);
    const presign = await publicHttp.post(`/portal/tickets/view/${token}/attachments/presign`, {
      filename: file.name,
      contentType,
    });
    const { uploadUrl, fileUrl, path } = presign.data.data;

    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.statusText}`);

    return { filename: file.name, url: fileUrl, size: file.size, mimeType: contentType, path };
  },
};
